// /src/js/locales/en.js

export default {
  // Core common actions and states
  common: {
    // Actions
    save: 'Save',
    logout: 'Log out',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    close: 'Close',
    change: 'Change',

    // States
    loading: 'Loading...',
    active: 'Active',
    inactive: 'Inactive',
    retry: 'Retry',

    // Messages
    error: 'Error',
    success: 'Success',
    warning: 'Warning',

    // Common UI
    actions: 'Actions',
    search: 'Search',
    all: 'All',
    optional: 'optional',
    characters: 'characters',
  },

  // Global common translations
  global: {
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    change: 'Change',
    saving: 'Saving...',
    hide: 'Hide',
    showPreview: 'Show Preview',
  },

  // Form-related translations
  form: {
    name: 'Name',
    description: 'Description',
    duration: 'Duration',
    price: 'Price',
    currency: 'Currency',
    category: 'Category',
    service: 'Service',
    icon: 'Icon',
    color: 'Color',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    role: 'Role',

    // Validation
    required: 'This field is required',
    invalidPhone: 'Invalid phone number',
    invalidEmail: 'Invalid email address',
    positive: 'Value must be greater than 0',
  },

  // Business/Salon related
  salon: {
    name: 'Salon Name',
    description: 'Salon Description',
    services: 'Services',
    categories: 'Categories',
    serviceCategories: 'Service Categories',
    professionals: 'Professionals',
    businessHours: 'Business Hours',
    contact: 'Contact Information',
  },

  // Services management (admin)
  services: {
    title: 'Salon Services',
    subtitle: "Manage your salon's services and categories",
    allServices: 'All Services',
    searchPlaceholder: 'Search services...',

    // Dialogs
    addService: 'Add New Service',
    editService: 'Edit Service',
    addCategory: 'Add New Category',
    editCategory: 'Edit Category',

    // Table headers
    tableHeaders: {
      service: 'Service',
      category: 'Category',
      duration: 'Duration',
      price: 'Price',
      active: 'Active',
      actions: 'Actions',
    },

    // Form labels
    serviceName: 'Service Name',
    serviceDescription: 'Service Description',
    serviceDuration: 'Duration (minutes)',
    servicePrice: 'Price',
    categoryName: 'Category Name',
    categoryDescription: 'Category Description',

    // Placeholders
    serviceNamePlaceholder: "Women's Cut",
    categoryPlaceholder: 'e.g., Hair Services, Nail Care, Skincare',
    descriptionPlaceholder: 'Brief description of this category',

    // Options
    currencies: {
      usd: 'USD ($)',
      eur: 'EUR (€)',
      gbp: 'GBP (£)',
      mxn: 'MXN ($)',
    },

    icons: {
      scissors: 'Scissors',
      hand: 'Hand',
      face: 'Face',
      brush: 'Brush',
      spa: 'Spa',
    },

    colors: {
      red: 'Red',
      blue: 'Blue',
      green: 'Green',
      orange: 'Orange',
      teal: 'Teal',
      purple: 'Purple',
    },
  },

  syncStatus: {
    title: 'Sync Status',
    online: 'Online',
    offline: 'Offline',
    lastSync: 'Last synced',
    pendingSyncs: '{count} pending changes',
    syncNow: 'Sync Now',
    syncCurrent: 'Sync Current Salon',
    syncAll: 'Sync All Salons',
    syncCurrentSuccess: 'Current salon synced successfully',
    syncCurrentFailed: 'Failed to sync current salon',
    syncAllSuccess: 'All salons synced successfully',
    syncAllPartial: 'Some salons failed to sync',
    syncError: 'Error during sync operation',
  },

  sideBar: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    profile: 'Profile',
    prospects: 'Prospects',
    salonConfig: 'Salon Admin Dashboard',
    salonWizard: 'Salon Wizard',
    login: 'Login',
    logout: 'Logout',
    contact: 'Contact',
  },

  salonSettings: {
    title: 'Salon Settings',
    subtitle: 'Manage your salon information and preferences',
    saveChanges: 'Save Changes',
    currentTheme: 'Active Theme',
    editSettings: 'Edit Settings',

    sections: {
      basicInformation: 'Basic Information',
      addressInformation: 'Address Information',
      themeColors: 'Theme Colors',
      salonLogos: 'Salon Logos',
      quickActions: 'Quick Actions',
    },

    fields: {
      publicUrlSlug: 'Public URL Slug',
      salonDescription: 'Salon Description',
      streetNumber: 'Number',
      neighborhood: 'Neighborhood',
      primaryLogo: 'Primary Logo',
      secondaryLogo: 'Secondary Logo',
      openingTime: 'Opening Time',
      closingTime: 'Closing Time',
    },

    hints: {
      publicUrl: 'This will be your public salon URL',
      salonDescription: 'This will be displayed on your public salon page',
    },

    actions: {
      previewTheme: 'Preview Theme',
      viewPublicPage: 'View Public Page',
      uploadLogo: 'Upload Logo',
      resetToDefaults: 'Reset',
    },

    validation: {
      phone: 'Invalid phone number',
      slug: 'Only lowercase letters, numbers, and hyphens allowed',
    },

    messages: {
      resetConfirm: 'Are you sure you want to reset all settings to defaults? This action cannot be undone.',
      themePreview: 'Theme preview applied for 3 seconds',
      logoUploadSuccess: '{type} logo uploaded successfully',
      logoUploadError: 'Failed to upload {type} logo',
      settingsLoaded: 'Salon settings loaded successfully',
      settingsSaved: 'Settings saved successfully',
      settingsLoadError: 'Failed to load salon settings',
      settingsSaveError: 'Failed to save settings',
      settingsReset: 'Settings reset to defaults',
    },

    resetDialog: {
      title: 'Reset Settings',
      message: 'Are you sure you want to reset all settings to their original values?',
      description:
        'This will restore all form fields to the data saved in your salon profile. Any unsaved changes will be lost.',
      confirmButton: 'Reset Settings',
    },
  },

  salonWizard: {
    title: 'Salon Setup Wizard',
    saveAndContinue: 'Save and Continue',
    next: 'Next',
    back: 'Back',
    skip: 'Skip',
    finish: 'Finish',
    creatingProfile: 'Creating your profile...',
    salonCreatedSuccess: 'Salon created successfully!',
    salonCreationError: 'Error creating salon: {error}',

    // Stepper step names
    steps: {
      businessInfo: 'Business Info',
      location: 'Location',
      categories: 'Categories',
      services: 'Services',
      visualIdentity: 'Visual Identity',
      visualStyle: 'Visual Style',
      photos: 'Photos',
      final: 'Final',
    },

    // Alerts
    alerts: {
      saveSuccess: 'Changes saved successfully',
      saveError: 'Error saving changes',
    },

    // Business Info Step
    businessInfo: {
      title: 'Business Information',
      subtitle: 'Tell us about your salon. We want to know the essentials to create your website.',
      businessName: 'Business Name',
      businessNamePlaceholder: 'e.g. Salon Glamour',
      businessType: 'Business Type',
      description: 'Brief Description (optional)',
      descriptionPlaceholder: 'Describe your business in a few words...',
      staffCount: 'Number of Professionals',
      selectCount: 'Select range',
      workingHours: 'Working Hours',
      workingDays: 'Service Days',
      types: {
        salonDeBelleza: 'Beauty Salon',
        barberia: 'Barbershop',
        spa: 'Spa',
        estetica: 'Aesthetics',
        centroBelleza: 'Beauty Center',
      },
    },

    // Location Step
    location: {
      title: 'Location',
      subtitle: 'Where is your salon? This will help customers find you easily on the map.',
      country: 'Country',
      state: 'State',
      city: 'City',
      street: 'Street and Number',
      streetPlaceholder: 'Av. 10 con 15 no. 5',
      postalCode: 'Postal Code',
      mapLocation: 'Map Location',
      mapPlaceholder: 'Place the marker at the correct location.',
      searchLocation: 'Search your location',
      manualEntry: 'Write it manually',
      orEnterManually: 'Or enter manually',
      selectState: 'Select State',
      selectCity: 'Select City',
      selectPostalCode: 'Select Postal Code',
      useCurrentLocation: 'Use my current location',
      gettingLocation: 'Getting your location...',
      loadingMap: 'Loading map...',
      mapError: 'Error loading map',
      retryMap: 'Retry',
      countries: {
        mexico: 'Mexico',
        usa: 'United States',
        canada: 'Canada',
      },
    },

    // Visual Style Step
    visualStyle: {
      title: 'Visual Style',
      subtitle:
        "Give personality to your site. Choose the palette that best represents your brand. Don't worry, you can change it later.",
      preview: 'Website Preview',
      sampleSalonName: 'Your Salon Name',
      menuHome: 'Home',
      menuServices: 'Services',
      menuAbout: 'About',
      menuContact: 'Contact',
      welcomeText: 'Welcome to Your Salon',
      taglineText: 'Your beauty destination',
      bookNowText: 'Book Now',
      themes: {
        elegantMinimalist: {
          name: 'Elegant & Minimalist',
          description: 'Sophisticated, professional, timeless',
        },
        modernYouthful: {
          name: 'Modern & Youthful',
          description: 'Colorful, fresh, dynamic',
        },
        relaxedNatural: {
          name: 'Relaxed & Natural',
          description: 'Organic, warm, welcoming',
        },
        luxuriousBold: {
          name: 'Luxurious & Bold',
          description: 'Impactful, premium, with character',
        },
        inclusiveVibrant: {
          name: 'Inclusive & Vibrant',
          description: 'Diverse, cheerful, positive',
        },
      },
    },



    // Common elements
    basicInfo: 'Basic Information',
    basicInfoDescription:
      'Enter the basic information about your salon. This information will be displayed to your customers.',
    logo: 'Logo',
    logoDescription:
      "Upload your salon logo. This will be displayed on your salon's website, app, and marketing materials.",
    banner: 'Banner',
    bannerDescription:
      "Upload a banner image for your salon. This will be displayed at the top of your salon's website and app.",
    themeColors: 'Theme Colors',
    themeColorsDescription:
      "Choose a theme for your salon. These colors will be used throughout your salon's website and app.",
    categories: 'Service Categories',
    services: 'Services',
    complete: 'Complete',
    salonDetails: 'Salon Details',
    salonName: 'Salon Name',
    address: 'Address',
    street: 'Street Address',
    city: 'City',
    state: 'State/Province',
    postalCode: 'Postal Code',
    country: 'Country',
    phoneNumber: 'Phone Number',
    email: 'Email',
    businessHours: 'Business Hours',
    open: 'Open',
    closed: 'Closed',
    uploadLogo: 'Upload Logo',
    uploadBanner: 'Upload Banner',
    primaryColor: 'Primary Color',
    secondaryColor: 'Secondary Color',
    addCategory: 'Add Category',
    addService: 'Add Service',
    categoryName: 'Category Name',
    serviceName: 'Service Name',
    categoryDescription: 'Category Description',
    serviceDescription: 'Service Description',
    categoryActions: 'Category Actions',
    serviceActions: 'Service Actions',
    serviceDuration: 'Duration (minutes)',
    servicePrice: 'Price',
    noCategories: 'No categories added yet',
    addFirstCategory: 'Add First Category',
    deleteConfirmTitle: 'Delete Category',
    deleteConfirmMessage: 'Are you sure you want to delete this category? This action cannot be undone.',
    useDefaultTitle: 'Use Default Categories?',
    useDefaultMessage: 'Would you like to use our recommended default categories? You can customize them afterward.',
    useDefault: 'Use Default Categories',
    editCategory: 'Edit Category',
    deleteCategory: 'Delete Category',
    isActive: 'Active',
    selectCategory: 'Select a Category',
    selectCategoryPrompt: 'Please select a category to add services',
    servicesIn: 'Services in',
    minutes: 'min',
    currency: 'Currency',
    editService: 'Edit Service',
    deleteService: 'Delete Service',
    noServices: 'No services added yet',
    addFirstService: 'Add First Service',
    congratulations: 'Congratulations!',
    setupComplete: 'Your salon setup is complete',
    visitSalon: 'Visit Your Salon',
    logoRequirements: 'Recommended size: 500x500 pixels. Maximum file size: 2MB. Supported formats: JPG, PNG, SVG.',
    bannerRequirements: 'Recommended size: 1200x400 pixels. Maximum file size: 5MB. Supported formats: JPG, PNG.',
    dragOrClick: 'Drag and drop your file here or click to browse',
    browseLogo: 'Browse Files',
    browseBanner: 'Browse Files',
    logoUsage: 'How your logo will be used',
    logoUsageWebsite: 'Website Header',
    logoUsageApp: 'Mobile App',
    logoUsageEmail: 'Email Communications',
    logoUsageInvoice: 'Invoices & Receipts',
    bannerUsage: 'How your banner will be used',
    bannerUsageWebsite: 'Website Header',
    bannerUsageApp: 'Mobile App',
    bannerUsageSocial: 'Social Media',
    bannerUsageEmail: 'Email Marketing',
    bookNow: 'Book Now',
    serviceCategories: 'Service Categories',
    salonTagline: 'Beauty & Wellness',
    change: 'Change',
    workingHours: 'Working Hours',
    workingDays: 'Service Days',
    servicesOffered: 'Services Offered',
    searchPhoto: 'Search',
    takePhoto: 'Take photo',
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    // Theme categories
    themeCategories: {
      professional: 'Professional',
      quirky: 'Quirky',
      modern: 'Modern',
      sophisticated: 'Sophisticated',
      natural: 'Natural',
    },
    // Theme names
    themeNames: {
      executive: 'Executive',
      corporate: 'Corporate',
      precision: 'Precision',
      playful: 'Playful',
      vibrant: 'Vibrant',
      eclectic: 'Eclectic',
      minimal: 'Minimal',
      urban: 'Urban',
      digital: 'Digital',
      luxe: 'Luxe',
      elegance: 'Elegance',
      opulence: 'Opulence',
      botanical: 'Botanical',
      serene: 'Serene',
      earthy: 'Earthy',
    },

    // Validation messages
    validation: {
      businessNameRequired: 'Business name is required',
      businessNameTooShort: 'Business name must be at least 2 characters',
      streetRequired: 'Street address is required',
      cityRequired: 'City is required',
      stateRequired: 'State is required',
      categoryNameRequired: 'Category name is required',
      categoryNameExists: 'Category name already exists',
      categoryRequired: 'At least one category is required',
      serviceNameRequired: 'Service name is required',
      serviceNameExists: 'Service name already exists in this category',
      durationRequired: 'Duration is required',
      priceRequired: 'Price must be 0 or greater',
      fullNameRequired: 'Full name is required',
      invalidImageType: 'Please select a valid image file (JPG, PNG, SVG)',
      fileTooLarge: 'File size must be less than {size}',
      fileTooLargeGeneric: 'File is too large',
    },

    // Service Categories Step
    serviceCategoriesStep: {
      title: 'Service Categories',
      subtitle: 'Define the main categories for your salon services',
      suggested: 'Suggested Categories',
      selected: 'Selected Categories',
      addCategory: 'Add Category',
      editCategory: 'Edit Category',
      addNewCategory: 'Add New Category',
      loadDefaults: 'Load Default Categories',
      categoryName: 'Category Name',
      categoryDescription: 'Category Description',
      categoryPlaceholder: 'e.g., Hair Services, Nail Care, Skincare',
      customCategoryPlaceholder: 'e.g. Colorimetry, Barbershop...',
      descriptionPlaceholder: 'Brief description of this category',
      noCategories: 'No categories added yet',
      noCategoriesDescription: 'Start by adding your first service category',
      deleteConfirm: 'Delete Category?',
      categories: {
        hairServices: 'Hair Services',
        nailServices: 'Nail Services',
        facialServices: 'Facial Services',
        bodyServices: 'Body Services',
        hairCuts: 'Hair Cuts',
        beardServices: 'Beard Services',
        stylingServices: 'Styling Services',
        massageServices: 'Massage Services',
        bodyTreatments: 'Body Treatments',
        facialTreatments: 'Facial Treatments',
        wellnessServices: 'Wellness Services',
        skinTreatments: 'Skin Treatments',
        bodyContouring: 'Body Contouring',
        aestheticProcedures: 'Aesthetic Procedures',
        makeupServices: 'Makeup Services',
      },
    },

    // Detailed Services Step
    detailedServices: {
      title: 'Detailed Services',
      subtitle:
        'Add your services one by one. This will appear on your website and allow your customers to book easily.',
      selectCategory: 'Select Category',
      selectCategoryPrompt: 'Please select a category to add services',
      servicesIn: 'Services in',
      serviceName: 'Service Name',
      serviceNamePlaceholder: "Women's Cut",
      duration: 'Duration',
      cost: 'Cost',
      minutes: 'min',
      addService: 'Add Service',
      addAnotherService: 'Add another service',
      editService: 'Edit Service',
      deleteConfirm: 'Are you sure you want to delete this service?',
      noServicesInCategory: 'No services in this category yet',
      noCategoriesTitle: 'No Categories Available',
      noCategoriesMessage: 'Please add service categories first before adding detailed services.',
      defaultServicesDialog: {
        title: 'Load Default Services?',
        message:
          "It looks like you don't have any services set up yet. Would you like to load some default services for your categories to get started?",
        subtitle: 'You can always edit or remove these services later.',
        skip: 'Skip',
        loadServices: 'Load Default Services',
      },
    },

    // Professionals Step
    professionals: {
      title: 'Professionals',
      subtitle: 'Meet your team. Add the professionals who work with you to show their availability and services.',
      addAnotherProfessional: 'Add another professional',
      addProfessional: 'Add Professional',
      editProfessional: 'Edit Professional',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Oscar Rosado',
      role: 'Role or Specialty',
      rolePlaceholder: 'Colorist',
      paymentModality: 'Payment Modality',
      paymentModalityPlaceholder: 'Commission',
      phone: 'Phone',
      phonePlaceholder: '+52 984 169 7256',
      photo: 'Photo (Optional)',
      photoRequirements: 'Recommended size: 500x500 pixels. Maximum file size: 2 MB. Supported formats: JPG, PNG.',
      personalInfo: 'Personal Information',
      services: 'Services',
      uploadPhoto: 'Drop, upload or paste image',
      noProfessionals: 'No professionals added yet',
      noProfessionalsDescription: "Add your team members to showcase your salon's expertise",
      deleteConfirm: 'Are you sure you want to delete this professional?',
    },

    // Hours and Service Step
    hours: {
      title: 'Opening Hours',
      subtitle: 'Define when your salon is available',
      businessHours: 'Business hours:',
      opening: 'Opening',
      closing: 'Closing',
      serviceDays: 'Service days:',
      professionalsLabel: 'professional(s)',
    },

    // Visual Identity Step
    visualIdentity: {
      title: 'Visual Identity',
      subtitle: 'Time to show your best face! Upload your logo and a cover image for your site.',
      logo: 'Logo',
      logoRequirements: 'Maximum file size: 2 MB. Supported formats: JPG, PNG, SVG. (500x500 pixels)',
      uploadLogo: 'Upload logo',
      banner: 'Banner',
      bannerRequirements: 'Maximum file size: 2 MB. Supported formats: JPG, PNG. (1200x400 pixels)',
      uploadBanner: 'Upload banner',
      bannerDescription:
        "This will be the first thing your customers see. If you don't have it yet, you can add it later.",
      noLogo: "I don't have a logo, create one later",
      upload: 'Upload',
      dragOrClick: 'Drag and drop your file here or click to browse',
      preview: 'Preview',
      bannerPlaceholder: 'Your banner image will appear here',
      colorPalette: 'Color Palette:',
      suggestedColors: 'Suggested colors',
      colorsAutoApply: 'These colors will be automatically applied to your website',
      websitePreview: 'Website preview:',
      styles: {
        minimalista: {
          name: 'Minimalist',
          description: 'Clean and elegant',
        },
        elegante: {
          name: 'Elegant',
          description: 'Sophisticated and premium',
        },
        juvenil: {
          name: 'Youthful',
          description: 'Vibrant and modern',
        },
        clasico: {
          name: 'Classic',
          description: 'Traditional and timeless',
        },
      },
    },

    // Gallery Step
    gallery: {
      title: 'Image Gallery',
      subtitle: 'Show your work with 3-5 photos (max. 2MB each)',
      addImages: 'Add images ({count}/5):',
      uploadImages: 'Upload images',
      formatRequirements: 'JPG, PNG, WEBP - Max. 2MB per image',
      clickOrDrag: 'Click or drag files here',
      noImagesYet: "You haven't added images to your gallery yet",
      imageRequirements: 'Image requirements:',
      minimum3Images: 'Minimum 3 images',
      maximum5Images: 'Maximum 5 images ✓',
      formats: 'Formats: JPG, PNG, WEBP ✓',
      maxSize: 'Max size: 2MB per image ✓',
      needMoreImages: 'You need to add at least {count} more image{plural} to continue.',
      websitePreview: 'Website preview',
      previewDescription: 'See how your photos will look on the website',
      hidePreview: 'Hide preview',
      showPreview: 'Show preview',
    },

    // Website Preview Component
    preview: {
      title: 'Preview of Your Website',
      subtitle: 'This is how customers will see your salon',
      location: 'Location',
      openNow: 'Open now',
      featuredServices: 'Featured Services',
      from: 'From',
      gallery: 'Gallery',
      galleryPhotoAlt: 'Gallery photo',
      morePhotos: '+{count} more',
      bookAppointment: 'Book Appointment',
      contact: 'Contact',
      styleLabel: 'Style:',
      interactivePreview: '✨ Interactive Preview',
      previewDescription: 'This is a simulation of how your website will look with the selected style. Colors, layout and elements will be automatically applied according to your choice.',
      responsiveDesign: '• Responsive design',
      customColors: '• Custom colors',
      logoAndBanner: '• Logo and banner included',
      defaultServices: {
        haircut: 'Cut and Style',
        coloring: 'Hair Coloring',
        facialTreatment: 'Facial Treatments',
      },
    },

    // Salon Photos Step
    salonPhotos: {
      title: 'Salon Photos',
      subtitle:
        'Make your salon look amazing. Add real photos of your space. This inspires confidence and attracts more customers.',
      uploadPhotos: 'You can upload up to 6 photos (interior, exterior)',
      photoRequirements: 'Maximum file size: 2 MB. Supported formats: JPG, PNG. (290x280 pixels)',
      selectFromGallery: 'Select 6 images from your gallery',
      upload: 'Upload',
      addPhoto: 'Add Photo',
      addMorePhotos: 'Add More Photos',
      removePhoto: 'Remove photo',
      photosUploaded: 'photos uploaded',
      tipsTitle: 'Photography Tips',
      tip1: "Show your salon's best angles and lighting",
      tip2: 'Include both interior and exterior shots',
      tip3: 'Capture your work stations and waiting area',
      tip4: 'Make sure photos are well-lit and high quality',
    },

    // Policies and Payments Step
    policies: {
      title: 'Policies and Payments',
      subtitle: 'Define your service terms and payment methods',
      cancellationPolicy: 'Cancellation policies',
      cancellationPolicyPlaceholder: 'Describe your cancellation, booking policies, etc.',
      suggestedPolicies: 'Suggested policies',
      paymentMethodsTitle: 'Accepted payment methods',
      termsAndConditions: 'Terms and conditions',
      acceptServiceTerms: 'I accept the terms and conditions of service',
      acceptServiceTermsDescription: 'By checking this box, you confirm that you have read and accept our terms of service.',
      acceptPrivacyPolicy: 'I accept the privacy policy',
      acceptPrivacyPolicyDescription: 'I accept the processing of my personal data according to the privacy policy.',
      dataProtection: 'Data protection',
      dataProtectionDescription: 'Your data is protected and used only for the operation of your profile.',
      flexiblePolicies: 'Flexible policies',
      flexiblePoliciesDescription: 'You can modify your policies at any time from your control panel.',
      validationError: 'You must accept the terms and conditions and privacy policy to continue.',
      defaultCancellationPolicy: 'Cancellations must be made at least 24 hours in advance.',
      paymentMethods: {
        cash: 'Cash',
        card: 'Debit/Credit card',
        transfer: 'Bank transfer',
        digital: 'Digital payments (PayPal, etc.)',
      },
      suggestedPoliciesList: {
        cancellation24h: 'Cancellations must be made at least 24 hours in advance.',
        noPets: 'Pets are not allowed on the premises.',
        punctuality: 'Client must arrive on time for their appointment.',
        deposit50: '50% deposit required for services over $500.',
      },
    },

    // Final Step
    final: {
      title: 'Publish Profile',
      subtitle: 'Your salon is ready to go live!',
      reviewTitle: 'Final Review',
      reviewSubtitle: 'Review all information before publishing your profile',
      congratulations: 'Congratulations!',
      setupComplete: 'Your salon setup is complete',
      publishProfile: 'Publish my profile',
      summaryTitle: 'Setup Summary',
      previewTitle: 'Website Preview',
      businessInformation: 'Business Information',
      schedule: 'Schedule',
      selectedStyle: 'Selected style:',
      imagesAdded: 'images added',
      noPolicies: 'No policies defined',
      readyToPublish: 'Ready to publish!',
      profileVisibilityMessage: 'Your profile will be visible to potential customers immediately after publication.',
      noLocationDefined: 'No location defined',
      locationUnspecified: 'Location not specified',
      notDefined: 'Not defined',
      professionals: 'professionals',
      categories: 'categories',
      services: 'services',
      teamMembers: 'team members',
      photos: 'photos',
      noAddress: 'No address provided',
      bannerPlaceholder: 'Your banner will appear here',
      featuredServices: 'Featured Services',
    },
  },

  dashboard: {
    loading: {
      salonData: 'Loading salon data...',
    },
    welcome: {
      happeningToday: "Here's what's happening with {salonName} today",
    },

    // Period filters (NEW)
    periods: {
      today: 'Today',
      week: 'Week',
      month: 'Month',
      custom: 'Custom',
    },

    // Sales section (NEW)
    sales: {
      total: 'Total Sales',
      vsYesterday: 'vs yesterday',
      byProfessional: 'Sales by Professional',
    },

    // Professional sales (NEW)
    professional: {
      sales: 'Professional Sales',
    },

    // Transactions (NEW)
    transactions: {
      types: 'Transaction Types',
      cash: 'Cash',
      card: 'Card',
      transfer: 'Transfer',
    },

    // Appointments (NEW)
    appointments: {
      today: "Today's Appointments",
      time: 'Time',
      noAppointments: 'No appointments for today',
    },

    // Quick actions (NEW)
    actions: {
      newBooking: 'New Booking',
      addSale: 'Add Sale',
    },

    load_failed: "We could not load your bookings.",
    retry: "Retry",
    status: {
      active: 'Active',
      inactive: 'Inactive',
      acceptingBookings: 'Currently accepting bookings',
      salonInactive: 'Salon is inactive',
    },
    stats: {
      activeServices: 'Active Services',
      readyToBook: 'Ready to book',
      serviceCategories: 'Service Categories',
      organized: 'Organized',
      todaysBookings: "Today's Bookings",
      revenueToday: 'Revenue Today',
      trendUp: '+15% from yesterday',
      trendDown: '-5% from yesterday',
    },
    bookings: {
      recent: 'Recent Bookings',
      viewAll: 'View All',
      headers: {
        customer: 'Customer',
        service: 'Service',
        time: 'Time',
        status: 'Status',
        actions: 'Actions',
      },
    },
    quickActions: {
      title: 'Quick Actions',
      addService: {
        title: 'Add New Service',
        subtitle: 'Create a new service offering',
      },
      viewBookings: {
        title: 'View All Bookings',
        subtitle: 'Manage appointments',
      },
      updateInfo: {
        title: 'Update Salon Info',
        subtitle: 'Edit salon details',
      },
      viewPublic: {
        title: 'View Public Page',
        subtitle: 'See how customers see your salon',
      },
    },
    salonStatus: {
      title: 'Salon Status',
      status: 'Status',
      todaysHours: "Today's Hours",
      contact: 'Contact',
      manageSettings: 'Manage Settings',
    },
  },

  // Admin Navigation
  adminNavigation: {
    title: 'Admin Dashboard',
    subtitle: 'Manage your salon operations',
    tabs: {
      dashboard: 'Dashboard',
      clients: 'Clients',
      schedule: 'Schedule',
      staff: 'Staff',
    },
  },

  // Bookings/Schedule
  bookings: {
    title: 'Schedule',
    addButton: 'Add Appointment',
    noAppointments: 'No appointments for this day',
    viewDetails: 'View Details',
  },

  // Staff Management
  staff: {
    title: 'Staff',
    loading: 'Loading staff members...',
    inviteStaff: 'Invite Staff',
    filters: {
      all: 'All',
      frequent: 'Frequent',
    },
    load_failed: "We could not load your bookings.",
    retry: "Retry",
    status: {
      available: 'Available',
      busy: 'Busy',
      off: 'Off',
    },
    emptyState: 'No staff members found',
    invitations: {
      title: 'Pending Invitations',
      pending: 'Pending',
      sent: 'Sent',
      expired: 'Expired',
      cancel: 'Cancel',
      resend: 'Resend',
      emptyState: 'No pending invitations',
      confirmCancel: 'Are you sure you want to cancel the invitation for {email}?',
    },
  },

  // Staff Invitation Modal
  staffInvitation: {
    emailPlaceholder: 'Enter staff member email',
    selectRole: 'Select a role',
    personalMessage: 'Personal Message',
    messagePlaceholder: 'Add a personal welcome message (optional)',
    sending: 'Sending...',
    sendInvitation: 'Send Invitation',
    sendError: 'Failed to send invitation. Please try again.',
  },

  // Staff Roles
  staffRoles: {
    stylist: 'Stylist',
    barber: 'Barber',
    colorist: 'Colorist',
    manicurist: 'Manicurist',
    esthetician: 'Esthetician',
    receptionist: 'Receptionist',
    manager: 'Manager',
    other: 'Other',
  },

  // Validation Messages
  validation: {
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    roleRequired: 'Please select a role',
  },

  // Error Messages
  errors: {
    noSalonSelected: 'No salon selected. Please select a salon first.',
    authenticationRequired: 'Authentication required. Please log in again.',
  },

  // Client Management
  clients: {
    title: 'Clients',
    addButton: 'Add Client',
    searchPlaceholder: 'Search by name or phone...',
    lastVisit: 'Last visit',
    filters: {
      all: 'All',
      frequent: 'Frequent',
      new: 'New',
      inactive: 'Inactive',
    },
    emptyState: 'No clients found',
    noResults: 'No clients match your search',
  },

  // Booking Wizard
  bookingWizard: {
    title: 'New Booking',
    step: 'Step',
    of: 'of',
    close: 'Close',
    progress: 'Booking progress',
    submitting: 'Creating your booking...',
    back: 'Back',
    continue: 'Continue',
    exitConfirm: 'Are you sure you want to exit? Your progress will be lost.',
    exitStay: 'Stay here',
    exitLeave: 'Leave',
    whatsapp: {
      cta: 'Book via WhatsApp',
      message: 'Hi! I would like to book {services} for {day} at {time}. Can you confirm my appointment?',
    },

    // Step 1: Service Selection
    step1: {
      title: 'Choose your service',
      subtitle: 'Select the service you want to book',
      searchPlaceholder: 'Search services...',
      allCategories: 'All',
      noServicesFound: 'No services found',
      noServicesHint: 'Try a different category or search',
      // The catalog states (#47): loading, and honestly-empty — distinct from
      // the search-found-nothing copy above.
      loadingServices: 'Loading services...',
      noCatalog: 'This salon has not published its services online yet',
      noCatalogHint: 'Contact the salon directly to book',
      noBookable: 'Book these services directly with the salon',
      noBookableHint: 'Contact the salon to choose a service and arrange your appointment.',
      contactSalon: 'Contact the salon',
      backToSalon: 'Back to the salon',
      selectedService: 'Selected service',
      minutes: 'minutes',
      servicesSelected: 'services',
      pickOneHint: 'Pick a service to continue',
    },

    // Step 2: Professional Selection
    step2: {
      title: 'Choose your professional',
      noStaffAvailable: 'No professionals available',
      pickNoPreferenceHint: 'Select "No preference" to continue',
      subtitle: 'Select a professional or let us assign the best one available',
      noPreference: 'No preference',
      pickOneHint: 'Pick a professional to continue',
      noPreferenceDesc: 'We will assign you the best professional available for your schedule',
      selectedProfessional: 'Selected professional',
      reviews: 'reviews',
      experience: 'experience',
      availableSlots: 'available slots',
    },

    // Step 3: Date & Time
    step3: {
      title: 'Select date and time',
      subtitle: 'Choose the day and time that works best for you',
      pickSlotHint: 'Pick a day and a time',
      sessionOne: '{name} · {min} min',
      sessionMany: '{count} services · {min} min total',
      selectDate: 'Appointment date',
      selectTime: 'Available time',
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      available: 'available',
      loadingSlots: 'Loading available times...',
      selectDateFirst: 'Select a date to see available times',
      noSlotsAvailable: 'No times available for this date',
      tryAnotherDate: 'Try another date or professional',
      // Why-specific empty answers (#43). The {weekday} arrives already
      // pluralized ("Thursdays") — see weekdayPlural in Step3DateTime.
      closedThisWeekday: 'The salon is closed on {weekday}',
      closedForToday: 'The salon has already closed for today',
      chooseAnotherDay: 'Pick another day',
      dayFull: 'This day is fully booked',
      closedDay: 'Closed',
      // The fetch itself failed (network / edge down). Distinct from a day
      // that is simply full: telling someone "no times" when we never managed
      // to ask sends them away from a salon that may be wide open.
      slotsUnavailable: 'We could not load the times',
      slotsRetry: 'Check your connection or try again in a moment',
      timezoneNotice:
        "Your timezone differs from {salon}. The times shown are the salon's local time.",
      selectedAppointment: 'Selected appointment',
    },

    // Step 4: Confirmation
    step4: {
      title: 'Confirm your booking',
      privacyNotice: 'Your information is protected and never shared with third parties',
      servicesSummary: '{count} services · {min} min total',
      completeRequired: 'Please complete all required fields (*)',
      verifyEmailToContinue: 'Verify your email to continue.',
      verifyOrClearEmail: "Verify your email to continue, or clear it if you'd rather not register it.",
      nameRequired: 'Name is required',
      emailRequired: 'Email is required',
      emailInvalid: 'Invalid email address',
      subtitle: 'Review details and complete your information',
      contactInfo: 'Contact Information',
      fullName: 'Full name',
      fullNamePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: "example{'@'}email.com",
      phone: 'Phone',
      phonePlaceholder: '+52 55 1234 5678',
      verifyPhone: 'Verify your mobile number',
      verifyPhoneHelp: 'You will need to send us a WhatsApp message to validate your phone.',
      returningVerified: 'Booking with your already-verified contact info',
      verifyOtherContact: 'Want to verify a different email and phone?',
      verifyOtherPhone: 'Want to verify a different phone number?',
      checkingVerified: 'Checking your verified info…',
      verifyOtherEmail: 'Want to verify a different email?',
      termsAcceptLabel: 'I declare that I am of legal age and I accept the',
      termsAcceptLink: 'Terms and Conditions and the Privacy Notice',
      termsThenYourData: 'Complete your details to continue:',
      termsRequiredNudge: 'You must accept the terms and conditions to complete your booking',
      termsAcceptedOn: 'You accepted the Terms and Conditions on {date}.',
      termsSeeAccepted: 'View the document you accepted',
      confirmFailed: 'We could not create your booking. The time slot may no longer be available; pick another and try again.',
      confirmRateLimited: 'Too many attempts for now. Wait a few minutes and try again.',
      confirmTransient: 'We could not complete your booking. Try again in a moment.',
      confirmExpired: 'Your booking hold expired. We set it up again: check the time and confirm once more.',
      confirmExpiredRetry: 'Your booking hold expired and we could not set it up again right now. Wait a moment and try again.',
      draftRateLimited: 'Too many attempts. Wait a few minutes and try again.',
      draftUnavailable: 'We could not prepare your booking. Try again in a moment.',
      phoneVerified: 'Phone verified',
      verifyEmail: 'Verify email',
      emailVerified: 'Email verified',
      emailCodeSent: 'We sent a 6-digit code to your email. Enter it here:',
      resendEmailCode: 'Resend code',
      emailCodeWrong: 'Wrong code. Check your email and try again.',
      emailCodeExpired: 'The code expired. Request a new one with "Resend code".',
      emailTooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
      emailTooManySends: 'We already sent several codes. Check your spam folder or wait a few minutes.',
      emailSendFailed: 'We could not send the code. Try again in a moment.',
      emailCodePlaceholder: '000000',
      specialPreferences: 'Special preferences (optional)',
      specialPreferencesHint:
        'Share anything that helps us give you a better experience',
      additionalNotes: 'Additional notes (optional)',
      notesPlaceholder: 'Do you have any special preference or comment?',
      paymentMethod: 'Payment method',
      cardPayment: 'Card',
      cardPaymentDesc: 'Pay now',
      cashPayment: 'Cash',
      cashPaymentDesc: 'Pay at salon',
      securityBadge: 'Secure payment processed with SSL encryption',
      bookingSummary: 'Booking summary',
      service: 'Service',
      professional: 'Professional',
      date: 'Date',
      time: 'Time',
      duration: 'min',
      subtotal: 'Subtotal',
      tax: 'Tax',
      total: 'Total',
      // The salon-conditions card renders the OWNER-written policy (#50);
      // no default claim exists on purpose — absent policy, absent card.
      salonConditions: 'Salon conditions',
      confirmBooking: 'Confirm booking',
      confirmingBooking: 'Confirming booking...',
    },

    // Success Screen
    success: {
      title: 'Booking registered!',
      message:
        'Your appointment has been successfully scheduled. You will receive a confirmation email with all the details.',
      bookingIdLabel: 'Booking ID',
      messageNoEmail:
        'Your booking is registered and the salon will confirm it soon. You can check its status right here.',
      bookingDetails: 'Booking Details',
      service: 'Service',
      professional: 'Professional',
      date: 'Date',
      time: 'Time',
      returnHome: 'Return to home',
      addToCalendar: 'Add to calendar',
    },
  },

  // Public vitrina — mirrors the es.js publicSalon block key-for-key. This
  // namespace did not exist in English at all (#24): vue-i18n returns the KEY
  // when a message is missing, so English-locale visitors saw literal
  // "publicSalon.layout.skipToContent"-style text, not a fallback language.
  // If you add a key on one side, add it on the other in the same change.
  publicSalon: {
    gallery: {
      // Neutral editorial fallback for the gallery heading — the salon's own
      // galleryTitle (gtt, #50) wins when set.
      defaultTitle: 'The art of feeling incredible',
    },
    team: {
      // Neutral fallback for the team heading — the salon's own teamTitle
      // (tmt, #51) wins when set; the section itself is opt-in via vst.
      defaultTitle: 'Expert hands',
    },
    // A service's own photos (gal, #153), in booking and on its home card.
    serviceGallery: {
      label: 'Photos of {name}',
      photoAlt: 'Photo {n} of {name}',
    },
    // Home
    home: {
      verified: 'Verified',
      reviews: 'reviews',
      reviewsCount: '1 review | {count} reviews',
      bookNow: 'Book now',
      // Header nav + the burger's site menu (#127).
      nav: {
        services: 'Services',
        gallery: 'Gallery',
        team: 'Team',
        location: 'Location',
        menu: 'Menu',
        close: 'Close',
        whatsapp: 'Message us',
        language: 'Language',
      },
      // Home sections, states and weekday names (#128).
      status: {
        openNow: 'Open now',
        open: 'Open',
        until: 'until {time}',
        todayUntil: 'Today until {time}',
        // A shift ending past midnight closes TOMORROW (#43): at 23:00 on
        // Monday, "Today until 2:00 AM" names an hour that is already gone.
        tomorrowUntil: 'Tomorrow until {time}',
        closed: 'Closed',
        // A shift that closes past midnight (#43): "9:00 PM - 2:00 AM" on its
        // own reads as a typo.
        closesNextDay: '{open} - {close} (next day)',
      },
      days: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      },
      sections: {
        services: {
          eyebrow: 'Services',
          title: 'Book your moment',
          subtitle: 'Pick a service and book your appointment online in under a minute.',
          all: 'All',
          ask: 'Ask for price',
          book: 'Book',
        },
        gallery: {
          eyebrow: 'Gallery',
          cta: 'Book your look',
        },
        team: {
          eyebrow: 'Team',
        },
        location: {
          eyebrow: 'Visit us',
          title: 'Hours and location',
          mapTitle: 'Location map',
          directions: 'Get directions',
        },
        closing: {
          title: 'Ready for your next appointment?',
          bookingLead: 'Book it in seconds — no calls, no waiting.',
          whatsappLead: 'Message us on WhatsApp and we will set up your appointment.',
        },
      },
      footer: {
        website: 'Website',
      },
      tabs: {
        about: 'About',
        services: 'Services',
        team: 'Team',
        reviews: 'Reviews',
      },
      description: 'Description',
      characteristics: 'Features',
      gallery: 'Gallery',
      noPhotos: 'No photos available',
      discount: 'Discount',
      popular: 'Popular',
      book: 'Book',
      noServices: 'No services available right now',
      viewAvailability: 'View availability',
      noTeam: 'No team information available right now',
      overallRating: 'Overall rating',
      ratingBreakdown: 'Rating breakdown',
      noReviews: 'No reviews available right now',
      lastVisit: 'Last visit',
    },

    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Discover our wide range of professional beauty and wellness services',
      allServices: 'All Services',
      popular: 'Popular',
      book: 'Book',
      min: 'min',
      noServices: 'No services found',
      noServicesHint: 'Try selecting a different category or come back later.',
      schedule: 'Hours',
      contact: 'Contact',
      location: 'Location',
      getDirections: 'Get directions',
      closed: 'Closed',
      // Same hours row as Home's, same overnight shift (#43).
      closesNextDay: '{open} - {close} (next day)',
      mapNotAvailable: 'Map not available',
    },

    // Layout (Footer & Header)
    layout: {
      back: 'Back',
      share: 'Share',
      favorite: 'Favorite',
      removeFavorite: 'Remove from favorites',
      skipToContent: 'Skip to main content',
      loading: 'Loading salon data',
      errorTitle: 'Could not load the salon',
      errorMessage: 'Check your connection and try again.',
      retry: 'Try again',
      disabledTitle: 'This salon is temporarily unavailable',
      disabledMessage: "This salon's online information is not available right now.",
      disabledBack: 'Back to home',
      newsletter: {
        title: 'Subscribe to our newsletter',
        placeholder: "example{'@'}mail.com",
        subscribe: 'Subscribe',
        thankYou: 'Thanks for subscribing!',
      },
      footer: {
        description:
          'Discover and book the best beauty services near you. Certified professionals, transparent prices and instant booking.',
        discover: 'Discover',
        nearSalons: 'Salons near you',
        topProfessionals: 'Top professionals',
        popularServices: 'Popular services',
        specialOffers: 'Special offers',
        forYou: 'For you',
        myBookings: 'My bookings',
        favorites: 'Favorites',
        history: 'History',
        paymentMethods: 'Payment methods',
        support: 'Support',
        helpCenter: 'Help center',
        terms: 'Terms and conditions',
        privacy: 'Privacy policy',
        contact: 'Contact',
        forBusiness: 'For business',
        registerSalon: 'Register your salon',
        dashboard: 'Dashboard',
        resources: 'Resources',
        api: 'API',
        email: 'Email',
        phone: 'Phone',
        office: 'Office',
        officeLocation: 'Mexico City, MX',
        copyright: 'All rights reserved.',
        privacyPolicy: 'Privacy',
        termsOfService: 'Terms',
        cookies: 'Cookies',
      },
      shareLink: {
        title: 'Share this salon',
        copied: 'Link copied to clipboard!',
      },
    },
  },
  // Customer menu — "My bookings" (#106)
  customerMenu: {
    title: "My bookings",
    open_menu: "My account",
    close: "Close",
    verified: "Verified",
    profile: "Profile",
    phone_label: "Phone",
    email_label: "Email",
    unverified: "Not verified",
    verify_cta: "Verify",
    loading: "Loading your bookings…",
    empty: "You have no upcoming bookings yet.",
    active: "Upcoming",
    other_salons: "Bookings at other salons",
    history: "History",
    history_loading: "Loading past bookings…",
    history_empty: "You have no past bookings.",
    name_label: "Name",
    name_edit: "Edit name",
    name_save: "Save",
    name_cancel: "Cancel",
    vf_preparing: "Preparing the verification…",
    vf_open_whatsapp: "Open WhatsApp",
    vf_waiting: "Waiting for the verification…",
    vf_send_code: "Send code",
    vf_code_label: "Code",
    vf_check_code: "Confirm",
    vf_done: "Verified!",
    vf_failed: "We could not start the verification. Please try again.",
    vf_rate_limited: "Too many attempts. Wait a few minutes and try again.",
    vf_bad_code: "The code is not correct.",
    vf_attempts_left: "attempts left: {n}",
    vf_code_expired: "The code expired. Request a new one.",
    vf_attempts_exhausted: "Too many attempts with this code. Request a new one.",
    vf_resend: "Change email / resend",
    vf_send_limit: "You reached the send limit. Try again later.",
    vf_reverify_cta: "Verify now",
    wipe_link: "Delete data on this device",
    wipe_confirm:
      "All data saved on this device will be deleted (session, name and preferences). Do you want to continue?",
    wipe_cta: "Delete",
    reverify: "For your security, please confirm your identity again to see your bookings.",
    write_salon: "Message the salon",
    wa_message: "Hi, I'm writing about my booking on {when} ({services}).",
    wa_booking_fallback: "booking",
    notification_now: "now",
    notify_confirmed_title: "Booking confirmed!",
    notify_confirmed_body: "{salon} confirmed your booking for {when}.",
    notify_cancelled_title: "Booking cancelled",
    notify_cancelled_body: "{salon} cancelled your booking for {when}. Tap to book another time.",
    load_failed: "We could not load your bookings.",
    retry: "Retry",
    status: {
      pending: "Pending",
      confirmed: "Confirmed",
      completed: "Completed",
      cancelled: "Cancelled",
      no_show: "No-show",
    },
  },
};
